/**
 * Bảng tông màu và khoá dịch của phân hệ License.
 *
 * Màu trạng thái **không** được quyết trong `src/mock/`: tầng đó bị xoá khi có
 * API thật, mà bảng tông phải sống lâu hơn nó.
 */
import type { BadgeVariant } from '../../components/ui';
import type { KhoaDich } from '../../i18n';
import type {
  KenhPhatHanh,
  KhoaTrongKeyring,
  KieuBanGiao,
  KieuYeuCau,
  LyDoDuyet,
  PlanCode,
  TrangThaiBanGiao,
  TrangThaiCaiDat,
  TrangThaiLicense,
  TopologyBoCai,
  TrangThaiYeuCau,
} from './types';

/**
 * Tông của `computed_status` — thang **trạng thái**, vì trạng thái license có
 * mức độ nặng nhẹ thật.
 *
 * `GRACE` là `danger`, khác `an_han` của gói dịch vụ (`warning`): hết ân hạn
 * thì máy khách on-premise chuyển chỉ đọc mà Control Plane không với tới để
 * cứu, nên đây là việc phải làm ngay. `REVOKED`/`SUPERSEDED` là `neutral`: bản
 * đã gỡ khỏi vòng đời, không còn gì để lo.
 */
export const TRANG_THAI_LICENSE: Record<TrangThaiLicense, { khoa: KhoaDich; variant: BadgeVariant }> = {
  NOT_YET_VALID: { khoa: 'license.trangThai.NOT_YET_VALID', variant: 'primary' },
  ACTIVE: { khoa: 'license.trangThai.ACTIVE', variant: 'success' },
  EXPIRING: { khoa: 'license.trangThai.EXPIRING', variant: 'warning' },
  GRACE: { khoa: 'license.trangThai.GRACE', variant: 'danger' },
  EXPIRED: { khoa: 'license.trangThai.EXPIRED', variant: 'danger' },
  REVOKED: { khoa: 'license.trangThai.REVOKED', variant: 'neutral' },
  SUPERSEDED: { khoa: 'license.trangThai.SUPERSEDED', variant: 'neutral' },
};

/** Trạng thái còn thu hồi được: bản đã gỡ hoặc đã hết hạn hẳn thì thu hồi vô nghĩa. */
export const THU_HOI_DUOC: readonly TrangThaiLicense[] = ['NOT_YET_VALID', 'ACTIVE', 'EXPIRING', 'GRACE'];

export const TRANG_THAI_YEU_CAU: Record<TrangThaiYeuCau, { khoa: KhoaDich; variant: BadgeVariant }> = {
  DRAFT: { khoa: 'license.trangThaiYeuCau.DRAFT', variant: 'neutral' },
  PENDING_APPROVAL: { khoa: 'license.trangThaiYeuCau.PENDING_APPROVAL', variant: 'warning' },
  APPROVED: { khoa: 'license.trangThaiYeuCau.APPROVED', variant: 'primary' },
  REJECTED: { khoa: 'license.trangThaiYeuCau.REJECTED', variant: 'danger' },
  ISSUED: { khoa: 'license.trangThaiYeuCau.ISSUED', variant: 'success' },
  CANCELLED: { khoa: 'license.trangThaiYeuCau.CANCELLED', variant: 'neutral' },
};

/** Loại yêu cầu là *loại*, không phải mức độ — dùng thang phân loại. */
export const KIEU_YEU_CAU: Record<KieuYeuCau, { khoa: KhoaDich; variant: BadgeVariant }> = {
  ISSUE: { khoa: 'license.kieu.ISSUE', variant: 'info' },
  RENEW: { khoa: 'license.kieu.RENEW', variant: 'teal' },
  REPLACE: { khoa: 'license.kieu.REPLACE', variant: 'violet' },
};

export const LY_DO_DUYET: Record<LyDoDuyet, KhoaDich> = {
  TERM_OVER_MAX: 'license.lyDoDuyet.TERM_OVER_MAX',
  SPECIAL_FEATURES: 'license.lyDoDuyet.SPECIAL_FEATURES',
  OVER_PLAN_QUOTA: 'license.lyDoDuyet.OVER_PLAN_QUOTA',
  RENEW_BEFORE_WINDOW: 'license.lyDoDuyet.RENEW_BEFORE_WINDOW',
};

export const TRANG_THAI_CAI_DAT: Record<TrangThaiCaiDat, { khoa: KhoaDich; variant: BadgeVariant }> = {
  PENDING: { khoa: 'license.trangThaiCaiDat.PENDING', variant: 'primary' },
  ACTIVE: { khoa: 'license.trangThaiCaiDat.ACTIVE', variant: 'success' },
  REPLACED: { khoa: 'license.trangThaiCaiDat.REPLACED', variant: 'neutral' },
  DECOMMISSIONED: { khoa: 'license.trangThaiCaiDat.DECOMMISSIONED', variant: 'neutral' },
};

export const CAC_PLAN_CODE: readonly PlanCode[] = ['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];

/** Thời hạn nhanh ở trình cấp (ADR-0058 mục 8). */
export const THOI_HAN_NHANH: readonly number[] = [12, 24, 36];

/** Loại đầu mối nhận thông báo hết hạn ở mốc 45 và 30 ngày (ADR-0058 mục 9). */
export const DAU_MOI_HOP_DONG = 'CONTRACT_OWNER';

/** Độ dài tối đa của lý do — cột `reason varchar(500)`. */
export const LY_DO_TOI_DA = 500;

/** Trạng thái khoá ký trong keyring (hợp đồng mục 7). Lộ khoá là việc khẩn nên `danger`. */
export const TRANG_THAI_KHOA: Record<KhoaTrongKeyring['status'], { khoa: KhoaDich; variant: BadgeVariant }> = {
  PREPUBLISHED: { khoa: 'license.trangThaiKhoa.PREPUBLISHED', variant: 'primary' },
  ACTIVE: { khoa: 'license.trangThaiKhoa.ACTIVE', variant: 'success' },
  RETIRED: { khoa: 'license.trangThaiKhoa.RETIRED', variant: 'neutral' },
  COMPROMISED: { khoa: 'license.trangThaiKhoa.COMPROMISED', variant: 'danger' },
};

/** Trần kích thước tệp keyring đọc ở trình duyệt, cùng trần tệp 64 KB của DT21 14.4. */
export const KEYRING_TOI_DA_BYTE = 64 * 1024;

/**
 * Kênh phát hành bộ cài (ADR-0107 mục 6) — thang **trạng thái**: `STABLE` là bản
 * đã thử, giao khách được; `BETA` còn chờ thử nội bộ.
 */
export const KENH_PHAT_HANH: Record<KenhPhatHanh, { khoa: KhoaDich; variant: BadgeVariant }> = {
  STABLE: { khoa: 'banGiao.kenh.STABLE', variant: 'success' },
  BETA: { khoa: 'banGiao.kenh.BETA', variant: 'warning' },
};

/**
 * Trạng thái phát hành chạy nền (ADR-0107 mục 6). `READY` không có huy hiệu:
 * bảng chỉ đánh dấu bản chưa dùng được, để bản thường không bị nhiễu.
 */
export const TRANG_THAI_PHAT_HANH: Record<'PROCESSING' | 'FAILED', { khoa: KhoaDich; variant: BadgeVariant }> = {
  PROCESSING: { khoa: 'banGiao.trangThaiPhatHanh.PROCESSING', variant: 'primary' },
  FAILED: { khoa: 'banGiao.trangThaiPhatHanh.FAILED', variant: 'danger' },
};

/**
 * Trạng thái bàn giao (ADR-0107 mục 5). `DELIVERED` là `warning`: hàng đã ở phía
 * khách mà chưa có bằng chứng, việc còn treo. `CUSTOMER_CONFIRMED` là `success`
 * nhưng nhãn nói "khách xác nhận", không nói "đã cài thành công" — bằng chứng
 * không ký số.
 */
export const TRANG_THAI_BAN_GIAO: Record<TrangThaiBanGiao, { khoa: KhoaDich; variant: BadgeVariant }> = {
  PREPARED: { khoa: 'banGiao.trangThai.PREPARED', variant: 'primary' },
  DELIVERED: { khoa: 'banGiao.trangThai.DELIVERED', variant: 'warning' },
  CUSTOMER_CONFIRMED: { khoa: 'banGiao.trangThai.CUSTOMER_CONFIRMED', variant: 'success' },
  FAILED: { khoa: 'banGiao.trangThai.FAILED', variant: 'danger' },
  CANCELLED: { khoa: 'banGiao.trangThai.CANCELLED', variant: 'neutral' },
};

/** Loại bàn giao là *loại*, không phải mức độ — thang phân loại. */
export const KIEU_BAN_GIAO: Record<KieuBanGiao, { khoa: KhoaDich; variant: BadgeVariant }> = {
  INSTALL: { khoa: 'banGiao.kieu.INSTALL', variant: 'info' },
  UPGRADE: { khoa: 'banGiao.kieu.UPGRADE', variant: 'teal' },
};

/** Mô hình triển khai của bộ cài (ADR-0094 mục 3). */
export const TOPOLOGY: Record<TopologyBoCai, KhoaDich> = {
  K3S_SINGLE_NODE: 'banGiao.topology.K3S_SINGLE_NODE',
  COMPOSE_MINIMAL: 'banGiao.topology.COMPOSE_MINIMAL',
};
